import React, { useState } from 'react';
import {
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
} from '@mui/material';
import moment from 'moment';
import PaginationComponent from '../../components/Pagination/Pagination';
import { useGetTeamComplianceStatus } from '../../hooks/queries/moodle/useGetTeamComplianceStatus';

const COMPLETION_STATE_COLORS = {
  EXCLUDED: 'default',
  PENDING: 'warning',
  COMPLETED: 'success',
  OVERDUE: 'error',
};

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  return moment(dateString).format('DD MMM YYYY, hh:mm A');
};

const ManagerCourseReportsComponent = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  const [employeeNameInput, setEmployeeNameInput] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [completionState, setCompletionState] = useState('');

  // Debounce employee name search
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setEmployeeName(employeeNameInput);
      setPage(0); // reset page on new search
    }, 500);
    return () => clearTimeout(handler);
  }, [employeeNameInput]);

  const { reports, totalPages, loading } = useGetTeamComplianceStatus({
    page,
    size: pageSize,
    state: completionState,
    employeeName: employeeName,
  });

  return (
    <Box p={2}>
      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Employee Name"
            variant="outlined"
            value={employeeNameInput}
            onChange={(e) => setEmployeeNameInput(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="completion-state-label">Completion State</InputLabel>
            <Select
              labelId="completion-state-label"
              label="Completion State"
              value={completionState}
              onChange={(e) => {
                setCompletionState(e.target.value);
                setPage(0); // reset page on filter change
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="NOT_STARTED">Not Started</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="NOT_ENROLLED">Not Enrolled</MenuItem>
              <MenuItem value="EXCLUDED">Excluded</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ overflowX: 'auto', mt: 2 }}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Employee Name
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Employee Code
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Course Name
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Year
              </TableCell>
              <TableCell sx={{ fontWeight: 600, px: 2 }}>State</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Deadline
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Completed At
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Last Synced At
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  No team compliance reports found.
                </TableCell>
              </TableRow>
            ) : (
              reports.map((row, index) => (
                <TableRow
                  key={`${row.employeeId}-${row.moodleCourseId}-${index}`}
                  hover
                >
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {row.employeeName}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {row.employeeCode}
                  </TableCell>
                  <TableCell sx={{ px: 2 }}>
                    {row.courseUrl ? (
                      <Link
                        href={row.courseUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {row.courseName}
                      </Link>
                    ) : (
                      row.courseName
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {row.year}
                  </TableCell>
                  <TableCell sx={{ px: 2 }}>
                    <Chip
                      label={row.completionState}
                      color={
                        COMPLETION_STATE_COLORS[row.completionState] ??
                        'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {formatDateTime(row.deadlineAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {formatDateTime(row.completedAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {formatDateTime(row.lastSyncedAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <PaginationComponent
          count={totalPages || 0}
          page={page + 1}
          pageSize={pageSize}
          pageChange={(event, value) => setPage(value - 1)}
          handlePageSizeChange={(event) => {
            setPageSize(event.target.value);
            setPage(0);
          }}
          paginationTitle="Rows per page"
        />
      </Box>
    </Box>
  );
};

export default ManagerCourseReportsComponent;
